/**
 * Secciones de la ficha que cuentan lo que ha pasado: mensajes, operativa y
 * registro de actividad.
 *
 * Sobre los mensajes: la conversación con el huésped NO existe todavía. El
 * WhatsApp que hay en el sistema es el de los leads de venta, con otro modelo
 * y otras reglas, y PRODUCT_V2_SPEC deja pendiente el modelo de contacto y
 * conversación del huésped. Esto es una maqueta: no envía nada y no llama a
 * ningún proveedor.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Broom, Wrench, ChatCircleText } from "@phosphor-icons/react";
import { issuesForVilla } from "@/lib/pmsSelectors";
import { MESSAGE_KINDS, ACTIVITY_LABEL } from "@/data/reservationsV2Demo";
import { REQUEST_STATUS_LABEL, AREA_LABEL } from "@/data/todayV2Demo";
import { StatusPill, QuickAction, Meta } from "@/pages/admin/pms/v2/V2Primitives";
import { DetailSection } from "@/pages/admin/pms/reservations/ReservationDetailSections";

/* -------------------------------------------------------------------------
 * Mensajes
 * ---------------------------------------------------------------------- */

export function MessagesSection({ view, onMessage }) {
  const messages = view.messages;
  return (
    <DetailSection
      title="Mensajes"
      testId="detail-messages"
      summary={messages.length === 0
        ? "Sin mensajes registrados"
        : `${messages.length} mensajes · último ${String(messages[0].at).slice(0, 10)}`}
    >
      {messages.length === 0 ? (
        <p className="text-sm text-stone-500 dark:text-stone-400">
          No hay comunicación registrada para esta reserva.
        </p>
      ) : (
        <ul className="space-y-2.5" data-testid="messages-list">
          {messages.map((m) => (
            <li key={`${m.at}-${m.title}`} className="flex items-start gap-2.5">
              <StatusPill tone={MESSAGE_KINDS[m.kind]?.tone || "stone"} dot={false}>
                {MESSAGE_KINDS[m.kind]?.label || m.kind}
              </StatusPill>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-stone-900 dark:text-stone-100">{m.title}</div>
                <div className="text-xs text-stone-600 dark:text-stone-300 mt-0.5">{m.preview}</div>
                <Meta items={[m.channel, String(m.at).slice(0, 16).replace("T", " ")]} />
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 pt-3 border-t border-stone-100 dark:border-stone-800">
        <QuickAction
          label="Mensaje al huésped"
          icon={ChatCircleText}
          onClick={onMessage}
          testId="messages-send"
        />
        <p className="text-[11px] text-stone-400 mt-1.5" data-testid="messages-note">
          No se envía nada. La conversación con el huésped necesita un modelo de contacto propio
          que todavía está por definir: el WhatsApp que existe hoy es el de los leads de venta y
          no se reutiliza aquí.
        </p>
      </div>
    </DetailSection>
  );
}

/* -------------------------------------------------------------------------
 * Operativa
 * ---------------------------------------------------------------------- */

/**
 * Solo lo que tiene que ver con ESTA estancia. Ni el módulo de limpieza ni el
 * de incidencias se reproducen aquí: se enseña lo relacionado y se enlaza.
 */
export function OperationsSection({ view, unitLabel }) {
  const r = view.reservation;
  const unit = view.assignedUnit;
  const issues = unit
    ? issuesForVilla(unit.villa_id).filter((i) => i.status !== "resolved")
    : [];
  const requests = view.operationalRequests;
  const cleanings = unit ? [
    { key: "arrival", label: "Repaso previo a la llegada", date: r.check_in },
    { key: "departure", label: "Limpieza de salida", date: r.check_out },
  ] : [];

  return (
    <DetailSection
      title="Operativa"
      testId="detail-operations"
      summary={unit
        ? `${cleanings.length} limpiezas · ${issues.length} incidencias · ${requests.length} peticiones`
        : `Sin ${unitLabel.singular.toLowerCase()} asignada todavía`}
    >
      {!unit && (
        <p className="text-sm text-amber-700 dark:text-amber-300">
          Hasta que no tenga {unitLabel.singular.toLowerCase()} asignada no hay trabajo operativo
          que preparar.
        </p>
      )}

      {unit && (
        <>
          <Block title="Limpieza" icon={Broom} to="/pms/housekeeping" testId="operations-housekeeping">
            <ul className="text-sm space-y-1">
              {cleanings.map((c) => (
                <li key={c.key} className="flex items-baseline justify-between gap-3">
                  <span className="text-stone-700 dark:text-stone-300">{c.label}</span>
                  <span className="text-[11px] text-stone-400 tabular-nums shrink-0">{c.date}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-stone-400 mt-1">
              Las tareas las genera el módulo de limpieza a partir de estas fechas.
            </p>
          </Block>

          <Block title="Mantenimiento" icon={Wrench} to="/pms/issues" testId="operations-issues">
            {issues.length === 0 ? (
              <p className="text-sm text-stone-500 dark:text-stone-400">
                Sin incidencias abiertas en {unit.villa_id}.
              </p>
            ) : (
              <ul className="text-sm space-y-1">
                {issues.map((i) => (
                  <li key={i.issue_id} className="flex items-start justify-between gap-3">
                    <span className="text-stone-700 dark:text-stone-300 min-w-0">{i.title}</span>
                    {i.blocks_inventory && (
                      <StatusPill tone="red" dot={false} testId={`operations-blocking-${i.issue_id}`}>
                        Bloquea disponibilidad
                      </StatusPill>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Block>
        </>
      )}

      <Block title="Peticiones del huésped" icon={ChatCircleText} to="/pms/today" testId="operations-requests">
        {requests.length === 0 ? (
          <p className="text-sm text-stone-500 dark:text-stone-400">Sin peticiones abiertas.</p>
        ) : (
          <ul className="text-sm space-y-1">
            {requests.map((q) => (
              <li key={q.request_id} data-testid={`operations-request-${q.request_id}`}>
                <span className="text-stone-700 dark:text-stone-300">{q.label}</span>
                <Meta items={[AREA_LABEL[q.area], REQUEST_STATUS_LABEL[q.status], q.requested_at]} />
              </li>
            ))}
          </ul>
        )}
      </Block>
    </DetailSection>
  );
}

function Block({ title, icon: Icon, to, children, testId }) {
  return (
    <div className="mb-3 last:mb-0" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-[10px] uppercase tracking-widest text-stone-400 inline-flex items-center gap-1.5">
          <Icon size={12} weight="bold" />
          {title}
        </div>
        <Link
          to={to}
          className="text-[11px] text-stone-500 hover:text-stone-900 dark:hover:text-stone-100 min-h-[44px] inline-flex items-center"
        >
          Abrir módulo
        </Link>
      </div>
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Actividad
 * ---------------------------------------------------------------------- */

/**
 * Quién cambió qué y cuándo. Es una maqueta, pero el concepto importa: sin
 * este registro nadie puede explicar después por qué una reserva acabó donde
 * acabó.
 */
export function ActivitySection({ view }) {
  const entries = view.activity;
  return (
    <DetailSection
      title="Actividad"
      testId="detail-activity"
      summary={`${entries.length} eventos registrados`}
    >
      {entries.length === 0 ? (
        <p className="text-sm text-stone-500 dark:text-stone-400">Sin actividad registrada.</p>
      ) : (
        <ol className="space-y-2" data-testid="activity-list">
          {entries.map((e) => (
            <li key={e.entry_id} className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-stone-300 dark:bg-stone-600 mt-1.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm text-stone-900 dark:text-stone-100">
                  {ACTIVITY_LABEL[e.kind] || e.kind}
                </div>
                <Meta items={[
                  e.detail,
                  e.actor,
                  e.at ? String(e.at).slice(0, 16).replace("T", " ") : null,
                ]} />
              </div>
            </li>
          ))}
        </ol>
      )}
    </DetailSection>
  );
}
